import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { COVERAGE_STATEMENT } from '@/legal/legalContent';
import {
  ShieldCheck, Sparkles, CheckCircle2, Clock, XCircle, AlertTriangle,
  Loader2, FileText, Calculator, Award, Lock, Info
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_BADGE = {
  elite: 'purple', premium: 'indigo', expanded: 'blue',
  low: 'emerald', standard: 'blue', elevated: 'amber',
  high: 'rose', declined: 'slate',
};
const STATUS_UI = {
  not_applied: { color: 'slate',   icon: Info,          label: 'Not applied' },
  pending:     { color: 'amber',   icon: Clock,         label: 'Under underwriter review' },
  approved:    { color: 'emerald', icon: CheckCircle2,  label: 'Approved & active' },
  declined:    { color: 'rose',    icon: XCircle,       label: 'Declined' },
  suspended:   { color: 'orange',  icon: AlertTriangle, label: 'Suspended' },
};

export default function Selle10Page() {
  const { token } = useAuth();
  const auth = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [state, setState] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);

  // Apply form
  const [legalName, setLegalName] = useState('');
  const [monthly, setMonthly] = useState('');
  const [avgVal, setAvgVal] = useState('');
  const [topCats, setTopCats] = useState('');
  const [applying, setApplying] = useState(false);

  // Eligibility form
  const [eCarrier, setECarrier] = useState('ups');
  const [eService, setEService] = useState('ups_ground');
  const [eOrigin, setEOrigin] = useState('');
  const [eDest, setEDest] = useState('');
  const [eCountry, setECountry] = useState('US');
  const [eValue, setEValue] = useState('');
  const [eCategory, setECategory] = useState('goods');
  const [eSig, setESig] = useState(false);
  const [eAdultSig, setEAdultSig] = useState(false);
  const [eStaffed, setEStaffed] = useState(false);
  const [ePackaging, setEPackaging] = useState(true);
  const [checking, setChecking] = useState(false);
  const [outcome, setOutcome] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, d] = await Promise.all([
        fetch(`${API}/selle10/status`, { headers: auth }).then(r => r.json()),
        fetch(`${API}/selle10/config`).then(r => r.json()),
        fetch(`${API}/selle10/decisions`, { headers: auth }).then(r => r.json()).catch(() => ({ records: [] })),
      ]);
      setState(s); setCfg(c); setDecisions(d.records || []);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const submitApplication = async () => {
    setApplying(true); setBanner(null);
    try {
      const r = await fetch(`${API}/selle10/apply`, {
        method: 'POST', headers: auth,
        body: JSON.stringify({
          legal_name: legalName,
          monthly_shipments: parseInt(monthly, 10) || 0,
          average_shipment_value: parseFloat(avgVal) || 0,
          top_categories: topCats.split(',').map(s => s.trim()).filter(Boolean),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Application failed');
      setBanner({ type: 'ok', text: d.message || 'Application submitted.' });
      await refresh();
    } catch (e) { setBanner({ type: 'err', text: e.message }); }
    finally { setApplying(false); }
  };

  const runEligibility = async () => {
    setChecking(true); setBanner(null); setOutcome(null);
    try {
      const r = await fetch(`${API}/selle10/eligibility`, {
        method: 'POST', headers: auth,
        body: JSON.stringify({
          carrier_code: eCarrier, service_code: eService,
          origin_zip: eOrigin, destination_zip: eDest,
          destination_country: eCountry,
          documented_value: parseFloat(eValue) || 0,
          merchandise_category: eCategory,
          signature_selected: eSig,
          adult_signature_selected: eAdultSig,
          staffed_pickup_selected: eStaffed,
          tracking_enabled: true,
          packaging_compliant: ePackaging,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Eligibility failed');
      setOutcome(d);
      await refresh();
    } catch (e) { setBanner({ type: 'err', text: e.message }); }
    finally { setChecking(false); }
  };

  if (loading || !cfg || !state) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;
  }

  const statusUi = STATUS_UI[state.status] || STATUS_UI.not_applied;
  const StatusIcon = statusUi.icon;
  const isApproved = state.status === 'approved';

  return (
    <div className="space-y-6" data-testid="selle10-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center">
            <Award className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              Selle10
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-mono uppercase tracking-wide">Up to $25k</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-mono uppercase tracking-wide">100% Chargeback</span>
            </h1>
            <p className="text-sm text-slate-500">Included secondary shipment protection · Underwritten by {cfg.underwriter}</p>
          </div>
        </div>
        <div className={`px-3 py-2 rounded-lg text-sm font-medium bg-${statusUi.color}-50 text-${statusUi.color}-700 border border-${statusUi.color}-200 flex items-center gap-2`} data-testid="selle10-status">
          <StatusIcon className="w-4 h-4" />
          {statusUi.label}
          {isApproved && state.tier && <span className="uppercase text-xs">· {state.tier} tier</span>}
        </div>
      </div>

      {banner && (
        <div className={`p-3 rounded-lg text-sm flex items-start gap-2 ${banner.type === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`} data-testid="banner">
          {banner.type === 'ok' ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertTriangle className="w-4 h-4 mt-0.5" />}
          <span>{banner.text}</span>
        </div>
      )}

      {/* Compliance disclosure */}
      <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 text-sm text-slate-600 dark:text-slate-400" data-testid="disclosure">
        <p className="flex items-start gap-2">
          <Lock className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" />
          <span>
            <b>Selle10 is a separate SKU.</b> Approved SelleSend shipments receive included secondary protection with limits <b>up to $25,000</b> per shipment, plus <b>100% chargeback coverage</b> (all subject to individual underwriting by {cfg.underwriter}).
            The actual approved limit is displayed <b>before shipment</b> based on carrier, service, merchandise, documented value, shipper history, origin/destination,
            packaging, acceptance method, delivery controls and chain-of-custody. International restrictions may apply.
            All protection is subject to the applicable insurer's policy, conditions, exclusions and deductible.
          </span>
        </p>
      </div>

      {/* APPLY block */}
      {state.status === 'not_applied' && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6" data-testid="apply-card">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-emerald-600" />Apply for Selle10</h3>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Legal business name"><input className="input" value={legalName} onChange={e => setLegalName(e.target.value)} data-testid="apply-name" /></Field>
            <Field label="Monthly shipments (approx)"><input className="input" type="number" value={monthly} onChange={e => setMonthly(e.target.value)} data-testid="apply-monthly" /></Field>
            <Field label="Average shipment value ($)"><input className="input" type="number" value={avgVal} onChange={e => setAvgVal(e.target.value)} data-testid="apply-avg" /></Field>
            <Field label="Top categories (comma-separated)"><input className="input" value={topCats} onChange={e => setTopCats(e.target.value)} placeholder="apparel, electronics" data-testid="apply-cats" /></Field>
          </div>
          <Button onClick={submitApplication} disabled={applying || !legalName || !monthly} className="mt-4 bg-gradient-to-r from-emerald-500 to-blue-500" data-testid="apply-btn">
            {applying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting…</> : <><FileText className="w-4 h-4 mr-2" />Submit application</>}
          </Button>
        </div>
      )}

      {state.status === 'pending' && (
        <div className="p-6 rounded-xl border-2 border-amber-200 bg-amber-50 dark:bg-amber-950/20 text-amber-800 flex items-start gap-3">
          <Clock className="w-5 h-5 mt-0.5" />
          <div>
            {/* Decia "under underwriter review" y que "Lloyd's syndicate typically
                responds within 24 business hours". La revision la hace SelleSend a
                mano. El aviso de cobertura es el texto de Mike (COVERAGE_STATEMENT). */}
            <p className="font-semibold">Your Selle10 application is under review.</p>
            <p className="text-sm mt-1">SelleSend reviews these by hand and usually answers within 24 business hours. You'll get an email + in-app notification when a decision is issued. {COVERAGE_STATEMENT}</p>
          </div>
        </div>
      )}

      {state.status === 'declined' && (
        <div className="p-6 rounded-xl border-2 border-rose-200 bg-rose-50 text-rose-800">
          <p className="font-semibold flex items-center gap-2"><XCircle className="w-5 h-5" />Selle10 application declined</p>
          {state.reviewer_notes && <p className="text-sm mt-2">{state.reviewer_notes}</p>}
        </div>
      )}

      {/* ELIGIBILITY */}
      {isApproved && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6" data-testid="eligibility-card">
          <h3 className="font-semibold mb-1 flex items-center gap-2"><Calculator className="w-4 h-4 text-emerald-600" />Pre-label eligibility check</h3>
          <p className="text-xs text-slate-500 mb-4">Run this before you buy the label. The exact approved limit is returned in real time.</p>
          <div className="grid md:grid-cols-4 gap-3">
            <Field label="Carrier">
              <select className="input" value={eCarrier} onChange={e => setECarrier(e.target.value)} data-testid="e-carrier">
                <option value="usps">USPS</option><option value="ups">UPS</option><option value="fedex">FedEx</option><option value="dhl">DHL</option><option value="webship">WebShip</option>
              </select>
            </Field>
            <Field label="Service"><input className="input" value={eService} onChange={e => setEService(e.target.value)} data-testid="e-service" /></Field>
            <Field label="Origin ZIP"><input className="input" value={eOrigin} onChange={e => setEOrigin(e.target.value)} data-testid="e-origin" /></Field>
            <Field label="Dest ZIP"><input className="input" value={eDest} onChange={e => setEDest(e.target.value)} data-testid="e-dest" /></Field>
            <Field label="Destination country"><input className="input" value={eCountry} onChange={e => setECountry(e.target.value.toUpperCase())} maxLength={2} data-testid="e-country" /></Field>
            <Field label="Documented value ($)"><input className="input" type="number" value={eValue} onChange={e => setEValue(e.target.value)} data-testid="e-value" /></Field>
            <Field label="Merchandise category">
              <select className="input" value={eCategory} onChange={e => setECategory(e.target.value)} data-testid="e-category">
                {Object.keys(cfg.categories).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <div className="flex items-center gap-3 pt-6 text-xs flex-wrap">
              <label className="flex items-center gap-1"><input type="checkbox" checked={eSig} onChange={e => setESig(e.target.checked)} data-testid="e-sig" />Signature</label>
              <label className="flex items-center gap-1"><input type="checkbox" checked={eAdultSig} onChange={e => setEAdultSig(e.target.checked)} data-testid="e-adult" />Adult sig</label>
              <label className="flex items-center gap-1"><input type="checkbox" checked={eStaffed} onChange={e => setEStaffed(e.target.checked)} data-testid="e-staffed" />Staffed pickup</label>
              <label className="flex items-center gap-1"><input type="checkbox" checked={ePackaging} onChange={e => setEPackaging(e.target.checked)} data-testid="e-pack" />Packaging OK</label>
            </div>
          </div>
          <Button onClick={runEligibility} disabled={checking || !eOrigin || !eDest || !eValue} className="mt-4 bg-gradient-to-r from-emerald-500 to-blue-500" data-testid="check-btn">
            {checking ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Working out your limit…</> : <><Sparkles className="w-4 h-4 mr-2" />Get exact approved limit</>}
          </Button>

          {outcome && (
            <div className={`mt-5 p-5 rounded-xl border-2 border-${TIER_BADGE[outcome.risk_tier]}-300 bg-${TIER_BADGE[outcome.risk_tier]}-50 dark:bg-${TIER_BADGE[outcome.risk_tier]}-950/20`} data-testid="outcome">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">{outcome.decision.replaceAll('_', ' ')} · {outcome.risk_tier} risk</p>
                  <p className="text-4xl font-bold tabular-nums">${outcome.approved_limit.toLocaleString()}</p>
                  {outcome.chargeback_coverage_percent > 0 && (
                    <p className="text-xs text-blue-700 font-semibold mt-1">+ {outcome.chargeback_coverage_percent}% chargeback coverage</p>
                  )}
                  <p className="text-sm mt-2 leading-relaxed">{outcome.disclosure_text}</p>
                </div>
                <ShieldCheck className={`w-12 h-12 text-${TIER_BADGE[outcome.risk_tier]}-500 shrink-0`} />
              </div>
              {outcome.missing_controls?.length > 0 && (
                <p className="text-xs mt-3 text-amber-700"><b>Missing controls:</b> {outcome.missing_controls.join(', ')}</p>
              )}
              <details className="mt-3">
                <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">Applied caps &amp; reasoning</summary>
                <div className="mt-2 text-xs text-slate-600 dark:text-slate-400 font-mono">
                  <p>caps: {JSON.stringify(outcome.applied_caps)}</p>
                  {(outcome.reasons || []).map((r, i) => <p key={i}>• {r}</p>)}
                </div>
                <p className="text-xs mt-2 text-slate-500 font-mono">signed_hash: {outcome.signed_hash}</p>
                <p className="text-xs text-slate-500 font-mono">decision_id: {outcome.decision_id}</p>
              </details>
            </div>
          )}
        </div>
      )}

      {/* DECISIONS LIST */}
      {isApproved && decisions.length > 0 && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6" data-testid="decisions-card">
          <h3 className="font-semibold mb-3">Recent decisions ({decisions.length})</h3>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500">
              <tr>
                <th className="px-2 py-2 text-left">Decision ID</th>
                <th className="px-2 py-2 text-left">Outcome</th>
                <th className="px-2 py-2 text-right">Approved</th>
                <th className="px-2 py-2 text-left">Tier</th>
                <th className="px-2 py-2 text-left">Certified</th>
                <th className="px-2 py-2 text-left">Issued</th>
              </tr>
            </thead>
            <tbody>
              {decisions.map(d => (
                <tr key={d.id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`decision-${d.id}`}>
                  <td className="px-2 py-2 font-mono text-xs">{d.id.slice(0, 8)}…</td>
                  <td className="px-2 py-2 text-xs">{d.outcome?.decision?.replaceAll('_', ' ')}</td>
                  <td className="px-2 py-2 text-right tabular-nums font-semibold">${d.outcome?.approved_limit?.toLocaleString() || 0}</td>
                  <td className="px-2 py-2 text-xs uppercase">{d.outcome?.risk_tier}</td>
                  <td className="px-2 py-2 text-xs">{d.certified ? '✓' : '—'}</td>
                  <td className="px-2 py-2 text-xs text-slate-500">{new Date(d.issued_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`.input{width:100%;height:38px;padding:0 10px;border-radius:8px;border:1px solid rgb(226 232 240);font-size:13px;background:transparent}
        .dark .input{border-color:rgb(51 65 85)}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wide text-slate-500 block mb-1">{label}</label>
    {children}
  </div>
);
